<template>
    <table>
        <thead class="thead">
        <tr>
            <th
                v-for="(titleHeader, index) in $tm('tiktok.table.connected_header')"
                :key="index"
                class="th"
                :class="columnClassList[index]"
            >
                <span class="headed_text">{{ titleHeader }}</span>
            </th>
        </tr>
        </thead>

        <tbody class="tbody" ref="scrollComponent">
        <tr v-for="account in accountList" :key="account.user_id">
            <td class="account">
                <div class="account-container">
                    <img class="icon" :src="tiktokIcon"/>
                    <div class="account_with_tags">
                        <span
                            class="username"
                            :title="account.username"
                            v-text="account.username"
                        />
                        <BaseTag :tag="account.tags?.[0]" typeClass="table"/>
                    </div>
                </div>
            </td>
            <td class="integration">
                <ul v-if="account.integrations?.length">
                    <li class="headed_text" v-for="crm in account.integrations" :key="crm">
                        {{ crm }}
                    </li>
                </ul>
                <span v-else>-</span>
            </td>
            <td class="paid_to">
                    <!-- !TODO: the end date is hidden for now, restore tariff_end_date here -->
                    <span>-</span>
            </td>
            <td class="tariff">
                <span v-text="account.tariff || '-'"/>
            </td>
            <td class="status" :class="statusAccount(account)?.status">
                <slot name="status" :account="account"/>
            </td>
            <td class="api-keys">
                <div class="api_key">{{ account.api_key }}</div>
            </td>
            <td class="settings">
                <slot name="settings" :account="account"/>
            </td>
            <td class="actions">
                <slot name="actions" :account="account"/>
            </td>
        </tr>
        <tr v-if="tableStatus">
            <td style="border-bottom: none" :colspan="columnClassList.length">
                <InfoDataTextRecurring :value="tableStatus" height="120px"/>
            </td>
        </tr>
        </tbody>
    </table>
</template>

<script setup>
// core
import {ref, onUpdated, onMounted, nextTick} from "vue";
import {useInfiniteScroll} from "@vueuse/core";

// components
import BaseTag from "@/components/ui/BaseTag.vue";
import InfoDataTextRecurring from "@/components/Recurring/InfoDataTextRecurring.vue";

// configs
import statusAccountConfig from "../configs/statusAccountConfig";

import tiktokIcon from "@/assets/icons/tiktok.svg";

const columnClassList = [
    "account",
    "integration",
    "paid_to",
    "tariff",
    "status",
    "api_keys",
    "settings",
    "actions",
];

const scrollComponent = ref(null);

const props = defineProps({
    accountList: {
        type: Array,
        default: () => [],
    },
    tableType: {
        type: String, // 'visible' || 'invisible'
        default: "visible",
    },
    // 'loading' | 'noAccounts' | null - null means the list has rows
    tableStatus: {
        type: String,
        default: null,
    },
});

const emits = defineEmits(["loadMore"]);

const {isLoading} = useInfiniteScroll(
    scrollComponent,
    () => {
        if (!props.accountList?.length || isLoading.value) return;
        emits("loadMore");
    },
    {
        distance: 50,
        interval: 300,
    }
);

const statusAccount = (account) => statusAccountConfig(account);

// the fade only makes sense on the usernames that really got cut off
const markClippedUsernames = async () => {
    await nextTick();

    scrollComponent.value?.querySelectorAll(".username").forEach((el) => {
        el.classList.toggle("is-clipped", el.scrollWidth > el.clientWidth + 1);
    });
};

onMounted(markClippedUsernames);
onUpdated(markClippedUsernames);
</script>

<style lang="scss" scoped>
@use "@/styles/table.scss";

.tbody {
    max-height: 300px;
    display: block;
    overflow-x: hidden;
    overflow-y: auto;
}

// mirrors .number / .number-container of table.scss so the column sits exactly
// where it does in the messenger tables
.account {
    min-width: 140px;
    margin: 0 auto;
    padding-left: 10px;

    &-container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 10px;
        width: 130px;
        margin: auto;
    }
}

.account_with_tags {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

// a username has no fixed length, so a long one scrolls inside its slot
// instead of stretching the column. Same treatment as the SIP password:
// no scrollbar, the clipped tail hinted with a fade.
.username {
    max-width: 100%;
    overflow-x: auto;
    white-space: nowrap;

    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }

    &.is-clipped {
        -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 20px), transparent 100%);
        mask-image: linear-gradient(to right, #000 calc(100% - 20px), transparent 100%);
    }
}

.tariff {
    width: 120px;
}

.icon {
    margin-top: 4px;
}

.settings {
    padding-left: 0;
    padding-right: 0;
    width: 100px;
}

::-webkit-scrollbar {
    width: 6px;
}
</style>
